import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '../../util_components/button';
import UploadImage from '../../util_components/upload_image';
import { post_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';

// Recommended character limits — hints only, not enforced. Mirrors the apis
// resolver truncation lengths so Ops sees the same guidance the crawler gets.
const LIMITS = {
  meta_title: 60,
  meta_description: 160,
  og_description: 200,
  twitter_description: 200
};

const OG_TYPE_OPTIONS = ['website', 'article', 'profile', 'video.other'];
const TWITTER_CARD_OPTIONS = ['summary', 'summary_large_image'];

// Site-wide default OG image (matches web_student single_workshop_detail.jsx) —
// used only in the preview fallback chain so the card is never empty.
const SITE_DEFAULT_OG_IMAGE =
  'https://cloudimages.myyogateacher.com/dgerdfai4/image/upload/f_auto,q_auto/v1659521340/website/OG-Image-v1.jpg';

const EMPTY_FORM = {
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  canonical_url: '',
  og_title: '',
  og_description: '',
  og_image: '',
  og_image_alt: '',
  og_type: 'website',
  twitter_card: 'summary_large_image',
  twitter_title: '',
  twitter_description: '',
  twitter_image: ''
};

// Keys sent in the update payload (workshop_uuid added at save time).
const PAYLOAD_KEYS = Object.keys(EMPTY_FORM);

const label_style = { color: 'var(--text-secondary)', fontSize: '13px' };
const input_style = {
  width: '100%',
  background: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  color: 'var(--input-text)',
  padding: '8px',
  borderRadius: '4px'
};
const field_wrap = { marginBottom: '16px', width: '100%' };
const section_title = {
  color: 'var(--text-primary)',
  fontWeight: 600,
  fontSize: '15px',
  margin: '20px 0 10px',
  borderBottom: '1px solid var(--border-default)',
  paddingBottom: '6px'
};

function counter_style(len, limit) {
  const over = limit && len > limit;
  return {
    fontSize: '11px',
    color: over ? 'var(--state-error)' : 'var(--text-muted)',
    marginTop: '2px'
  };
}

function WorkshopSeoMetadata(props) {
  const { workshop_uuid, slug, name, banner_image, set_notification_variable, close, onSaved } =
    props;

  const [form, set_form] = useState(EMPTY_FORM);
  const [loading, set_loading] = useState(true);
  const [saving, set_saving] = useState(false);
  const [load_error, set_load_error] = useState('');

  const load = useCallback(() => {
    set_loading(true);
    set_load_error('');
    const url = get_api_url('get_workshop_seo_metadata');
    post_api(url, { workshop_uuid }, true)
      .then((res) => {
        const seo = res?.data?.seo || {};
        // PROD-8869: hard-prefill — a saved non-blank value wins; otherwise pre-populate the field
        // with the resolved default (workshop name/description/banner/slug-derived canonical) the
        // public page would render, so Ops sees editable defaults instead of empty fields.
        const defaults = res?.data?.defaults || {};
        const next = { ...EMPTY_FORM };
        PAYLOAD_KEYS.forEach((k) => {
          const saved = seo[k];
          const has_saved = saved !== undefined && saved !== null && saved !== '';
          if (has_saved) next[k] = saved;
          else if (defaults[k] !== undefined && defaults[k] !== null) next[k] = defaults[k];
        });
        // og_type / twitter_card must stay non-empty for the selects.
        if (!next.og_type) next.og_type = 'website';
        if (!next.twitter_card) next.twitter_card = 'summary_large_image';
        set_form(next);
        set_loading(false);
      })
      .catch((e) => {
        let msg;
        try {
          msg = e.response.data.message;
        } catch (err) {
          msg = 'Failed to load SEO metadata. Please retry.';
        }
        set_load_error(msg);
        set_loading(false);
      });
  }, [workshop_uuid]);

  useEffect(() => {
    load();
  }, [load]);

  const on_change = (e) => {
    const { name: key, value } = e.target;
    set_form((prev) => ({ ...prev, [key]: value }));
  };

  // UploadImage calls change_input(image_type, url).
  const change_image = (image_type, image_url) =>
    set_form((prev) => ({ ...prev, [image_type]: image_url }));

  const save = () => {
    set_saving(true);
    const payload = { workshop_uuid };
    PAYLOAD_KEYS.forEach((k) => {
      payload[k] = form[k];
    });
    const url = get_api_url('update_workshop_seo_metadata');
    post_api(url, payload, true)
      .then(() => {
        // apis purges the prerender cache server-side after the upsert
        // (the token secret can't live in a browser SPA).
        set_notification_variable(true, 'success', 'SEO metadata saved');
        set_saving(false);
        if (onSaved) onSaved();
        close();
      })
      .catch((e) => {
        let msg;
        try {
          msg = e.response.data.message;
        } catch (err) {
          msg = 'Some error occured. Please contact dev team.';
        }
        set_notification_variable(true, 'error', msg);
        set_saving(false);
      });
  };

  const render_input = (key, labelText, { textarea } = {}) => {
    const limit = LIMITS[key];
    const val = form[key] || '';
    return (
      <div style={field_wrap}>
        <div style={label_style}>{labelText}</div>
        {textarea ? (
          <textarea
            name={key}
            value={val}
            onChange={on_change}
            style={{ ...input_style, height: '70px', resize: 'vertical' }}
          />
        ) : (
          <input name={key} value={val} onChange={on_change} style={input_style} />
        )}
        {limit ? (
          <div style={counter_style(val.length, limit)}>
            {val.length}/{limit} recommended
          </div>
        ) : null}
      </div>
    );
  };

  const render_select = (key, labelText, options) => (
    <div style={field_wrap}>
      <div style={label_style}>{labelText}</div>
      <select name={key} value={form[key]} onChange={on_change} style={input_style}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );

  // Resolve the values the social card / crawler would actually show, so the
  // preview matches the apis fallback chain (blank field → workshop → default).
  const preview_title = form.og_title || form.meta_title || name || '';
  const preview_description = form.og_description || form.meta_description || '';
  // Only https URLs are used as the preview background (mirrors the apis httpsOnly guard) so a
  // stored value can't break out of the CSS url("…") wrapper below.
  const is_https = (u) => typeof u === 'string' && /^https:\/\/[^"'()\s]+$/.test(u);
  const preview_image = [form.og_image, banner_image, SITE_DEFAULT_OG_IMAGE].find(is_https) || '';

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading SEO metadata…
      </div>
    );
  }

  if (load_error) {
    return (
      <div style={{ padding: '30px', textAlign: 'center' }}>
        <div style={{ color: 'var(--state-error)', marginBottom: '14px' }}>{load_error}</div>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxHeight: '75vh',
        overflowY: 'auto',
        color: 'var(--text-primary)',
        padding: '4px'
      }}
    >
      <h3 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>SEO Metadata</h3>
      <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '10px' }}>
        Workshop: {name}
        {slug ? ` (/workshops/${slug})` : ''}. Blank fields fall back to the workshop name,
        description and banner image.
      </div>

      {/* Live social-card preview */}
      <div style={section_title}>Social card preview</div>
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          overflow: 'hidden',
          maxWidth: '500px',
          background: 'var(--surface-card)',
          boxShadow: 'var(--shadow-card)',
          marginBottom: '10px'
        }}
      >
        <div
          style={{
            width: '100%',
            aspectRatio: '1200 / 630',
            background: 'var(--surface-sunken)',
            backgroundImage: preview_image ? `url("${preview_image}")` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div style={{ padding: '10px 12px' }}>
          <div
            style={{
              color: 'var(--text-primary)',
              fontWeight: 600,
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {preview_title || 'Untitled workshop'}
          </div>
          <div
            style={{
              color: 'var(--text-muted)',
              fontSize: '12px',
              marginTop: '2px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {preview_description}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
            myyogateacher.com
          </div>
        </div>
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '10px' }}>
        Recommended image size: 1200×630px.
      </div>

      {/* Reference: current banner image */}
      {banner_image ? (
        <div style={{ marginBottom: '16px' }}>
          <div style={label_style}>Used as OG image if left blank:</div>
          <img
            src={banner_image}
            alt="banner"
            style={{
              maxWidth: '200px',
              marginTop: '6px',
              border: '1px solid var(--border-default)',
              borderRadius: '4px'
            }}
          />
        </div>
      ) : null}

      {/* SEO */}
      <div style={section_title}>Search (SEO)</div>
      {render_input('meta_title', 'Meta title')}
      {render_input('meta_description', 'Meta description', { textarea: true })}
      {render_input('meta_keywords', 'Meta keywords (comma-separated)')}
      {render_input('canonical_url', 'Canonical URL')}

      {/* Open Graph */}
      <div style={section_title}>Open Graph (Facebook, LinkedIn, WhatsApp, Slack…)</div>
      {render_input('og_title', 'OG title')}
      {render_input('og_description', 'OG description', { textarea: true })}
      <div style={field_wrap}>
        <div style={label_style}>OG image (recommended 1200×630):</div>
        <UploadImage
          buttonTitle={form.og_image ? 'Change OG Image' : 'Upload OG Image'}
          imageUrl={form.og_image}
          uuid={workshop_uuid}
          folder_name="workshops"
          image_type="og_image"
          change_input={change_image}
          set_notification_variable={set_notification_variable}
        />
      </div>
      {render_input('og_image_alt', 'OG image alt text')}
      {render_select('og_type', 'OG type', OG_TYPE_OPTIONS)}

      {/* Twitter */}
      <div style={section_title}>Twitter / X</div>
      {render_select('twitter_card', 'Twitter card type', TWITTER_CARD_OPTIONS)}
      {render_input('twitter_title', 'Twitter title')}
      {render_input('twitter_description', 'Twitter description', { textarea: true })}
      <div style={field_wrap}>
        <div style={label_style}>Twitter image (recommended 1200×630):</div>
        <UploadImage
          buttonTitle={form.twitter_image ? 'Change Twitter Image' : 'Upload Twitter Image'}
          imageUrl={form.twitter_image}
          uuid={workshop_uuid}
          folder_name="workshops"
          image_type="twitter_image"
          change_input={change_image}
          set_notification_variable={set_notification_variable}
        />
      </div>

      <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        <Button disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="secondary" onClick={close}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

WorkshopSeoMetadata.propTypes = {
  workshop_uuid: PropTypes.string.isRequired,
  slug: PropTypes.string,
  name: PropTypes.string,
  banner_image: PropTypes.string,
  set_notification_variable: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  onSaved: PropTypes.func
};

WorkshopSeoMetadata.defaultProps = {
  slug: '',
  name: '',
  banner_image: '',
  onSaved: null
};

export default WorkshopSeoMetadata;
